<script setup lang="ts">
import { computed, inject, ref, watch, type Ref } from 'vue';
import { Plus, Trash2, ArrowUp, ChevronDown, ChevronRight, Pencil, Check, GripVertical, LocateFixed } from '@lucide/vue';
import Input from '@/components/ui/UIInput.vue';
import UISelect from '@/components/ui/UISelect.vue';
import Textarea from '@/components/ui/TextArea.vue';
import type { JsonSchemaField } from '@/types/PromptField';
import draggable from 'vuedraggable';

interface Props {
  field: JsonSchemaField;
  parentField?: JsonSchemaField;
  depth: number;
  disabled?: boolean;
}

interface Emits {
  (e: 'add-child', fieldId: string): void;
  (e: 'remove-field', fieldId: string, parentField?: JsonSchemaField): void;
  (e: 'update-field', fieldId: string, updates: Partial<JsonSchemaField>, parentField?: JsonSchemaField): void;
  (e: 'promote-field', fieldId: string, parentField: JsonSchemaField): void;
  (e: 'toggle-expansion', fieldId: string): void;
  (e: 'reorder-siblings', parentId: string | null | undefined, newOrder: JsonSchemaField[]): void;
}

// Required for recursive components
defineOptions({
  name: 'FieldItem',
});

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Inject parent component's methods and state
const fieldBuilderContext = inject('fieldBuilderContext') as {
  fieldTypeOptions: any;
  getFieldPath: (fieldId: string) => string;
  isFieldNameDuplicatedInLevel: (fieldName: string, siblings: JsonSchemaField[]) => boolean;
  getChildrenOf: (parentId: string) => JsonSchemaField[];
  getChildrenOfFiltered: (parentId: string) => JsonSchemaField[];
  getActualSiblings: (fieldId: string) => JsonSchemaField[];
  isUnderCoverageLines: (fieldId: string) => boolean;
  isItemNode: (field: { name: string; type: string }) => boolean;
  scrollToJson: (fieldId: string) => void;
  showExamplePayload: Ref<boolean>;
  expandToFieldId: Ref<string | null>;
  highlightedFieldId: Ref<string | null>;
  getAncestorIds: (fieldId: string) => string[];
};

// Indent per nesting level — smaller on mobile to preserve horizontal space.
// Applied via CSS variable so a media query (in <style>) can override.
const indentDepth = computed(() => props.depth);

const isCollapsed = ref(false);
const isEditing = ref(false);

const typeLabel = computed(() => {
  const t = props.field.type;
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
});

const children = computed(() => fieldBuilderContext.getChildrenOfFiltered(props.field.id));

// Visible children may belong to a placeholder (e.g. _coverageLineItem, _item)
// rather than directly to this field. They all share the same actual parentId,
// so use that for the reorder group + key. Falls back to this field's id when empty.
const childrenParentId = computed(() => children.value[0]?.parentId ?? props.field.id);

const isCollapsible = computed(() => (props.field.type === 'object' || props.field.type === 'array') && children.value.length > 0);

const canScrollToJson = computed(() => {
  if (fieldBuilderContext.showExamplePayload.value) return false;
  if (props.field.name.startsWith('_') && !fieldBuilderContext.isItemNode(props.field)) return false;
  return true;
});

const isHighlighted = computed(() => fieldBuilderContext.highlightedFieldId.value === props.field.id);

const hasDuplicateNameError = computed(
  () =>
    !fieldBuilderContext.isItemNode(props.field) &&
    fieldBuilderContext.isFieldNameDuplicatedInLevel(props.field.name, fieldBuilderContext.getActualSiblings(props.field.id)),
);

// Auto-expand when a descendant is targeted for scroll-to
watch(
  () => fieldBuilderContext.expandToFieldId.value,
  (targetId) => {
    if (!targetId || !isCollapsed.value) return;
    const ancestors = fieldBuilderContext.getAncestorIds(targetId);
    if (ancestors.includes(props.field.id)) {
      isCollapsed.value = false;
    }
  },
);

// Border colors for different nesting levels
const borderColor = computed(() => {
  const colors = [
    'border-slate-200', // Level 0 (root) - default gray
    'border-blue-300', // Level 1 - blue border
    'border-green-300', // Level 2 - green border
    'border-yellow-400', // Level 3 - yellow border
    'border-purple-300', // Level 4 - purple border
    'border-pink-300', // Level 5+ - pink border
  ];

  const colorIndex = Math.min(props.depth, colors.length - 1);
  return colors[colorIndex];
});
</script>

<template>
  <div
    :data-field-id="field.id"
    :style="{ '--indent-depth': indentDepth }"
    :class="[
      'field-item flex items-start gap-2 rounded border bg-white p-2 hover:bg-blue-50 transition-shadow duration-300',
      hasDuplicateNameError ? 'border-red-500' : borderColor,
      isHighlighted ? 'ring-2 ring-blue-400 shadow-md' : '',
    ]">
    <div class="flex-1 space-y-1.5">
      <!-- Field Header -->
      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="field-drag-handle flex h-4 w-4 shrink-0 cursor-grab items-center justify-center text-slate-300 hover:text-slate-500 active:cursor-grabbing"
          aria-label="Drag to reorder"
          title="Drag to reorder"
          @click.stop>
          <GripVertical class="h-3.5 w-3.5" />
        </button>
        <button
          v-if="isCollapsible"
          type="button"
          @click="isCollapsed = !isCollapsed"
          class="flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          :title="isCollapsed ? 'Expand' : 'Collapse'">
          <ChevronRight
            v-if="isCollapsed"
            class="h-3.5 w-3.5" />
          <ChevronDown
            v-else
            class="h-3.5 w-3.5" />
        </button>
        <!-- Field Meta -->
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span
              class="text-xs font-medium text-slate-700 break-all"
              :title="fieldBuilderContext.getFieldPath(field.id)">
              {{ field.name }}
            </span>
            <span
              v-if="!isEditing"
              class="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-slate-600 uppercase">
              {{ typeLabel }}
            </span>
            <label class="flex items-center gap-1 text-xs text-slate-600 hover:cursor-pointer select-none">
              <input
                :checked="field.required"
                @change="(e) => emit('update-field', field.id, { required: (e.target as HTMLInputElement).checked }, parentField)"
                type="checkbox"
                :disabled="disabled"
                class="h-3 w-3 rounded border-slate-300" />
              Required
            </label>
            <span
              v-if="isCollapsible && isCollapsed"
              class="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
              {{ children.length }} {{ children.length === 1 ? 'child' : 'children' }}
            </span>
            <span
              v-if="field.coverageTypeName"
              class="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
              {{ field.coverageTypeName }}
            </span>
            <span
              v-if="field.sublimitTypeName"
              class="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
              Sublimit · {{ field.sublimitTypeName }}
            </span>
            <span
              v-if="field.subretentionTypeName"
              class="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
              Subretention · {{ field.subretentionTypeName }}
            </span>
          </div>
          <div>
            <div
              v-if="!isEditing && field.description"
              class="text-xs text-slate-500 break-words"
              :title="field.description">
              {{ field.description }}
            </div>
            <span
              v-if="field.sourceField"
              class="block text-xs italic text-blue-600 break-words">
              From: {{ field.sourceField.displayName }}
            </span>
            <p
              v-if="hasDuplicateNameError"
              class="text-xs text-red-600">
              Duplicate field name
            </p>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="ml-auto flex items-center gap-1.5">
          <button
            v-if="canScrollToJson"
            @click.stop="fieldBuilderContext.scrollToJson(field.id)"
            class="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-400 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 hover:cursor-pointer"
            title="Show in JSON preview">
            <LocateFixed class="h-4 w-4" />
          </button>
          <button
            @click="isEditing = !isEditing"
            :disabled="disabled"
            class="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-700 hover:cursor-pointer"
            :title="isEditing ? 'Done editing' : 'Edit field'">
            <Check
              v-if="isEditing"
              class="h-4 w-4 text-green-600" />
            <Pencil
              v-else
              class="h-4 w-4" />
          </button>
          <button
            @click="emit('add-child', field.id)"
            :disabled="disabled"
            class="hover:cursor-pointer flex h-7 w-7 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-500 hover:bg-blue-100 hover:text-blue-700"
            :title="field.type === 'object' ? 'Add child field' : 'Add child field (will convert to object)'">
            <Plus class="h-4 w-4" />
          </button>
          <button
            v-if="parentField && !fieldBuilderContext.isUnderCoverageLines(field.id)"
            @click="emit('promote-field', field.id, parentField)"
            :disabled="disabled"
            class="flex items-center gap-1 rounded border border-purple-200 bg-purple-50 px-1.5 py-0.5 text-xs text-purple-600 hover:bg-purple-100 hover:cursor-pointer"
            title="Move up one level">
            <ArrowUp class="h-3 w-3" />
            Promote
          </button>
          <button
            @click="emit('remove-field', field.id, parentField)"
            :disabled="disabled"
            class="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-400 hover:border-red-200 hover:bg-red-50 hover:text-red-600 hover:cursor-pointer"
            title="Remove field">
            <Trash2 class="h-4 w-4" />
          </button>
        </div>
      </div>

      <!-- Field Configuration -->
      <div
        v-if="isEditing"
        class="grid grid-cols-[2fr_1fr] gap-2">
        <div>
          <Input
            :model-value="field.name"
            @update:model-value="(value) => emit('update-field', field.id, { name: value }, parentField)"
            placeholder="Field name"
            :disabled="disabled"
            :class="'h-8 py-1 ' + (hasDuplicateNameError ? 'border-red-500 ring-red-500' : '')"
            label="Name"
            size="sm" />
        </div>
        <UISelect
          :model-value="field.type"
          @update:model-value="
            (value) => emit('update-field', field.id, { type: value as 'string' | 'number' | 'boolean' | 'object' | 'array' }, parentField)
          "
          :options="fieldBuilderContext.fieldTypeOptions"
          :disabled="disabled"
          class="h-8 py-1"
          placeholder="Select type..." />
      </div>

      <Textarea
        v-if="isEditing"
        :model-value="field.description"
        @update:model-value="(value) => emit('update-field', field.id, { description: value }, parentField)"
        placeholder="Field description"
        :disabled="disabled"
        class="min-h-[36px] py-1.5 text-sm"
        label="Description" />
    </div>
  </div>

  <draggable
    v-if="!isCollapsed && children.length > 0"
    :model-value="children"
    @update:model-value="(v: JsonSchemaField[]) => emit('reorder-siblings', childrenParentId, v)"
    :group="`siblings-${childrenParentId}`"
    item-key="id"
    handle=".field-drag-handle"
    :animation="150"
    class="space-y-1">
    <template #item="{ element: child }">
      <div>
        <FieldItem
          :field="child"
          :parent-field="field"
          :depth="depth + 1"
          :disabled="disabled"
          @add-child="(fieldId) => emit('add-child', fieldId)"
          @remove-field="(fieldId, parentField) => emit('remove-field', fieldId, parentField)"
          @update-field="(fieldId, updates, parentField) => emit('update-field', fieldId, updates, parentField)"
          @promote-field="(fieldId, parentField) => emit('promote-field', fieldId, parentField)"
          @toggle-expansion="(fieldId) => emit('toggle-expansion', fieldId)"
          @reorder-siblings="(pid, order) => emit('reorder-siblings', pid, order)" />
      </div>
    </template>
  </draggable>
</template>

<style scoped>
/* Smaller indent on narrow viewports so deeply-nested fields don't push off screen. */
.field-item {
  margin-left: calc(var(--indent-depth, 0) * 10px);
}
@media (min-width: 768px) {
  .field-item {
    margin-left: calc(var(--indent-depth, 0) * 20px);
  }
}
</style>
